import { useCallback, useEffect, useRef } from 'react';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { fetchEmployees } from '../../../slices/employees/employeesSlice';
import { fetchSuppliers } from '../../../slices/suppliers/suppliersSlice';
import { fetchCustomers } from '../../../slices/customers/customersSlice';
import {
  clearDocuments,
  fetchDocuments,
  selectAllDocuments,
  setDocumentsPage,
} from '../../../slices/documents/documentsSlice';
import { selectUser } from '../../../slices/user/userSlice';
import { selectAllCompanies } from '../../../slices/companies/companiesSlice';
import {
  fetchBusinessProfileById,
  fetchBusinessProfileContactById,
} from '../../../slices/business-profile/businessProfileSlice';
import { DOCUMENTS_PER_PAGE } from '../../document-vault/hooks/vaultQueryParams';
import { KeyDatesTab, readKeyDatesQuery, useKeyDatesQuery } from './useKeyDatesQuery';

const useDocumentsKeyDates = () => {
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectAllCompanies)[0];
  const documentsCount = useAppSelector((state) => state.documents.documentsCount);
  const documentItems = useAppSelector(selectAllDocuments);
  const documentContractsLoading = useAppSelector((state) => state.documentContracts.status);
  const documentUploadsLoading = useAppSelector((state) => state.documents.status);
  const employeesLoading = useAppSelector((state) => state.employees.status);
  const customersLoading = useAppSelector((state) => state.customers.status);
  const suppliersLoading = useAppSelector((state) => state.suppliers.status);
  const businessProfileLoading = useAppSelector((state) => state.businessProfile.status);
  const currentUser = useAppSelector(selectUser);
  /*
   * The tab (Documents / Subscriptions) and the page live in the URL query
   * (?tab=subscriptions&page=2), so the browser Back button from a document details page
   * restores exactly the same view. Tab / page changes only update the URL; the documents are
   * fetched from it below.
   */
  const { tab: activeTab, page: documentsPage, updateKeyDatesQuery } = useKeyDatesQuery();

  const getNextPage = () => {
    updateKeyDatesQuery({ page: documentsPage + 1 });
  };

  const getRequestedPage = (passedPage: number) => {
    updateKeyDatesQuery({ page: passedPage });
  };

  const getPrevPage = () => {
    updateKeyDatesQuery({ page: Math.max(1, documentsPage - 1) });
  };

  const documents = [...documentItems];

  const handleTabChange = (tab: KeyDatesTab) => {
    updateKeyDatesQuery({ tab, page: 1 });
  };

  const getEmployees = useCallback(() => {
    if (company) {
      if (employeesLoading === 'idle') {
        dispatch(fetchEmployees({ company_id: company.id ?? 0 }));
      }
    }
  }, [employeesLoading, company, dispatch]);

  const getCustomers = useCallback(() => {
    if (company) {
      if (customersLoading === 'idle') {
        dispatch(fetchCustomers({ company_id: company.id ?? 0, page_size: 100 }));
      }
    }
  }, [customersLoading, company, dispatch]);

  const getSuppliers = useCallback(() => {
    if (company) {
      if (suppliersLoading === 'idle') {
        dispatch(fetchSuppliers({ company_id: company.id ?? 0, page_size: 100 }));
      }
    }
  }, [suppliersLoading, company, dispatch]);

  const getBusinessContacts = useCallback(() => {
    if (company) {
      if (businessProfileLoading === 'idle') {
        dispatch(fetchBusinessProfileContactById({ id: company.id ?? 0 }));
      }
    }
  }, [businessProfileLoading, company, dispatch]);

  const getBusinessDetails = useCallback(() => {
    if (company) {
      if (businessProfileLoading === 'idle') {
        dispatch(fetchBusinessProfileById({ id: company.id ?? 0 }));
      }
    }
  }, [businessProfileLoading, company, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearDocuments());
    };
  }, [dispatch]);

  useEffect(() => {
    getEmployees();
  }, [getEmployees]);

  useEffect(() => {
    getSuppliers();
  }, [getSuppliers]);

  useEffect(() => {
    getCustomers();
  }, [getCustomers]);

  useEffect(() => {
    getBusinessContacts();
  }, [getBusinessContacts]);

  useEffect(() => {
    getBusinessDetails();
  }, [getBusinessDetails]);

  const lastFetchedTabRef = useRef<KeyDatesTab | null>(null);

  // Fetch whatever the URL describes (tab + page).
  useEffect(() => {
    if (!currentUser.id) {
      return;
    }
    if (lastFetchedTabRef.current !== activeTab) {
      // Do not show documents of the previous tab while the new ones are loading.
      dispatch(clearDocuments());
      lastFetchedTabRef.current = activeTab;
    }
    dispatch(setDocumentsPage(documentsPage));

    const requestedTab = activeTab;
    const requestedPage = documentsPage;
    const isStillRequestedView = () => {
      const current = readKeyDatesQuery(new URLSearchParams(window.location.search));
      return current.tab === requestedTab && current.page === requestedPage;
    };

    dispatch(
      fetchDocuments({
        data: {
          user: currentUser.id,
          sort: 'key_date_end_date',
          order: 'ASC',
          hasKeyDate: true,
          type: requestedTab === 'Subscriptions' ? 'Subscription' : 'nonSubscription',
          page: requestedPage,
        },
      })
    )
      .unwrap()
      .then((result) => {
        // The page from the URL no longer exists (e.g. documents were deleted) - go to the last one.
        if (requestedPage > 1 && (result.results?.length ?? 0) === 0 && isStillRequestedView()) {
          const lastPage = Math.max(1, Math.ceil((result.count || 0) / DOCUMENTS_PER_PAGE));
          if (lastPage < requestedPage) {
            updateKeyDatesQuery({ page: lastPage }, { replace: true });
          }
        }
      })
      .catch(() => {
        // Out-of-range pages are rejected by the API - fall back to the first page.
        if (requestedPage > 1 && isStillRequestedView()) {
          updateKeyDatesQuery({ page: 1 }, { replace: true });
        }
      });
  }, [activeTab, documentsPage, currentUser.id, updateKeyDatesQuery, dispatch]);

  return {
    documents,
    documentContractsLoading,
    documentUploadsLoading,
    employeesLoading,
    suppliersLoading,
    customersLoading,
    businessProfileLoading,
    activeTab,
    documentsCount,
    documentsPage,
    getNextPage,
    getPrevPage,
    getRequestedPage,
    handleTabChange,
  };
};

export { useDocumentsKeyDates };
